import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Card, TextInput, Button, Text, Alert, Stack,
} from '@mantine/core'
import { IconAlertCircle, IconCheck, IconMail, IconMailForward } from '@tabler/icons-react'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { authApi } from '@/api/auth'
import { APP_VERSION } from '@/version'

const inputStyles = {
  label: { color: '#b6c2c9', fontWeight: 600, fontSize: 12, marginBottom: 4 },
  input: {
    backgroundColor: 'var(--ai-auth-input-bg)',
    borderColor: 'var(--ai-auth-border)',
    color: '#e9ecef',
    '&:focus': { borderColor: 'var(--ai-brand-verde)' },
  },
}

export function ForgotPassword() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) { setError('Informe seu e-mail.'); return }
    setError('')
    setLoading(true)
    try {
      await authApi.forgotPassword(email.trim().toLowerCase())
      setSubmitted(true)
    } catch {
      setError('Erro ao processar a solicitação. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'var(--ai-auth-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle ambient glow */}
      <Box
        style={{
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(9, 141, 147, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        {/* Brand */}
        <Stack align="center" mb="xl" gap={4}>
          <BrandLogo variant="horizontal" color="white" width={180} />
          <Text size="xs" c="rgba(255,255,255,0.35)" mt={8} fw={600} tt="uppercase" style={{ letterSpacing: '0.08em' }}>
            Recuperação de Senha
          </Text>
        </Stack>

        {/* Forgot password card */}
        <Card
          radius="md"
          p="xl"
          style={{
            backgroundColor: 'var(--ai-auth-card-bg)',
            border: '1px solid var(--ai-auth-border)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
          }}
        >
          {submitted ? (
            <>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <IconCheck size={22} color="#098d93" />
                <Text fw={700} size="lg" c="#e9ecef">Solicitação enviada</Text>
              </Box>
              <Text size="sm" c="#b6c2c9" mt="sm" mb="lg" lh={1.5}>
                Se o e-mail estiver cadastrado, você receberá um link com instruções para redefinir sua senha.
                Se não chegar em alguns minutos, confira a caixa de spam. São aceitos até 3 pedidos por hora;
                acima disso, nenhum e-mail novo é enviado.
              </Text>
              <Button
                fullWidth
                variant="subtle"
                color="teal"
                size="md"
                radius="sm"
                onClick={() => navigate('/login')}
                style={{ fontWeight: 700, fontSize: 13 }}
              >
                Voltar ao login
              </Button>
            </>
          ) : (
            <>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <IconMailForward size={22} color="#098d93" />
                <Text fw={700} size="lg" c="#e9ecef">Recuperar acesso</Text>
              </Box>
              <Text size="xs" c="#6c757d" mb="lg">
                Informe seu e-mail e enviaremos instruções para redefinir sua senha
              </Text>

              {error && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" radius="sm"
                  styles={{ root: { backgroundColor: 'rgba(255, 91, 87, 0.1)', border: '1px solid rgba(255, 91, 87, 0.2)' } }}
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack gap="sm">
                  <TextInput
                    label="E-mail"
                    placeholder="seu@email.com.br"
                    leftSection={<IconMail size={16} />}
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    autoComplete="email"
                    required
                    size="md"
                    styles={inputStyles}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    loading={loading}
                    mt={8}
                    size="md"
                    radius="sm"
                    style={{
                      backgroundColor: 'var(--ai-brand-verde)',
                      border: 'none',
                      fontWeight: 700,
                      fontSize: 13,
                      letterSpacing: '0.02em',
                      transition: 'all var(--ai-transition-base)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#077a80'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--ai-brand-verde)'
                    }}
                  >
                    Enviar link de reset
                  </Button>
                </Stack>
              </form>

              <Text
                size="xs" c="#098d93" ta="center" mt="lg" fw={600}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/login')}
              >
                Voltar ao login
              </Text>
            </>
          )}

          <Text size="xs" c="#51585e" ta="center" mt="xl" fw={600}>
            ConsoleAI v{APP_VERSION}
          </Text>
        </Card>

        <Text size="xs" c="rgba(255,255,255,0.2)" ta="center" mt="md" fw={500}>
          &copy; 2025-2026 Amazonas Inovare. All rights reserved.
        </Text>
      </Box>
    </Box>
  )
}
